import React from "react";
import { CheckCircle2, ListTodo, CalendarDays, Sparkles } from "lucide-react";
interface ProgressTrackerProps {
  daysDoneCount: number; // 0..7
  goalStepsDoneCount: number; // 0..9
}
export default function ProgressTracker({
  daysDoneCount,
  goalStepsDoneCount
}: ProgressTrackerProps) {
  const totalSteps = 16;
  const completedSteps = daysDoneCount + goalStepsDoneCount;
  const percent = Math.round(completedSteps / totalSteps * 100);

  // Honest, encouraging lines that change with progress and never promise outcomes
  let message = "A journey of grounding begins with a single deliberate five-minute move.";
  if (completedSteps === 0) {
    message = "No steps checked yet. Everything starts whenever you decide to take your first small move.";
  } else if (completedSteps <= 4) {
    message = "You have begun putting real feet on the ground. Steady rhythm matters far more than speed.";
  } else if (completedSteps <= 9) {
    message = "Solid momentum taking shape. Notice how bringing values into reality feels in your everyday hours.";
  } else if (completedSteps < totalSteps) {
    message = "Deep practice across both goals and daily habits. You are directly anchoring your inner ideals.";
  } else {
    message = "All 16 baseline steps completed! Celebrate this cycle and carry these grounding rhythms forward.";
  }
  return <div className="ql-glass p-4 sm:p-6 rounded-2xl border border-primary/30 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="size-4 text-primary shrink-0" />
            <h4 className="font-serif text-base sm:text-lg font-bold text-foreground">
              Grounding Progress Tracker
            </h4>
          </div>
          <p className="text-xs text-muted-foreground">
            Tracking your 7-day starter sequence and the three progress stages for each goal.
          </p>
        </div>

        <div className="flex items-baseline gap-2 self-start sm:self-auto shrink-0 font-mono">
          <span className="text-2xl sm:text-3xl font-bold text-primary">{completedSteps}</span>
          <span className="text-xs text-muted-foreground">of {totalSteps} steps ({percent}%)</span>
        </div>
      </div>

      {/* Accessible progress bar */}
      <div className="space-y-1.5">
        <div role="progressbar" aria-label="Action plan progress" aria-valuemin={0} aria-valuemax={totalSteps} aria-valuenow={completedSteps} aria-valuetext={`${completedSteps} of ${totalSteps} steps completed`} className="dtr-progress-bar-track">
          <div className="dtr-progress-bar-fill" style={{
          width: `${percent}%`
        }} />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] font-mono text-muted-foreground pt-0.5">
          <span className="inline-flex items-center gap-1.5">
            <CalendarDays className="size-3 text-primary shrink-0" />
            <span>Daily habits: {daysDoneCount}/7 days</span>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <ListTodo className="size-3 text-primary shrink-0" />
            <span>Goal checkpoints: {goalStepsDoneCount}/9 stages</span>
          </span>
        </div>
      </div>

      {/* Encouraging honest message */}
      <div className="p-3 rounded-xl bg-card/60 border border-border/60 text-xs text-muted-foreground flex items-start gap-2.5">
        <Sparkles className="size-3.5 text-primary shrink-0 mt-0.5" />
        <p className="leading-relaxed">{message}</p>
      </div>
    </div>;
}